import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BookOpen, Target, CheckCircle2, FileText, Calendar } from 'lucide-react';

// Dynamic Planner Tab Component
export const DynamicPlannerTab = ({ curriculumData, activeTerm }) => {
  if (!curriculumData || !curriculumData.terms) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <p className="text-muted-foreground">No curriculum data available.</p>
        </CardContent>
      </Card>
    );
  }

  const termIndex = parseInt(activeTerm.replace('term', '')) - 1;
  const termData = curriculumData.terms[termIndex];

  if (!termData) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <p className="text-muted-foreground">No data for {activeTerm.replace('term', 'Term ')}.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>{termData.term} - {curriculumData.class.name}</CardTitle>
            <CardDescription>
              {termData.assessment_count} assessments • {termData.total_marks} marks • ~{termData.estimated_weeks} weeks
            </CardDescription>
          </div>
          <Badge className="bg-primary">{termData.term}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Progress indicator */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground w-24">Term Progress:</span>
            <Progress value={(termData.estimated_weeks / 14) * 100} className="flex-1" />
            <span className="text-sm font-medium">{termData.estimated_weeks}/14 weeks</span>
          </div>

          {/* Assessments list */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">#</TableHead>
                  <TableHead>Assessment</TableHead>
                  <TableHead className="text-right">Marks</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {termData.assessments.map((assessment, index) => (
                  <TableRow key={assessment.id}>
                    <TableCell className="font-medium">{index + 1}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{assessment.title}</p>
                        {assessment.description && (
                          <p className="text-xs text-muted-foreground mt-1">{assessment.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold">{assessment.marks}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className="capitalize">{assessment.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Dynamic Scheme Tab Component
export const DynamicSchemeTab = ({ curriculumData }) => {
  if (!curriculumData || !curriculumData.strands) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <p className="text-muted-foreground">No strand data available.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Scheme of Work: {curriculumData.class.name}</CardTitle>
        <CardDescription>
          Curriculum structure organized by strands and units
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[600px] pr-4">
          <div className="space-y-6">
            {curriculumData.strands.map((strand) => (
              <div key={strand.id} className="border rounded-xl p-6 relative overflow-hidden">
                <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-primary"></div>
                
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline">Strand {strand.number}</Badge>
                      {strand.terms.map((term, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">{term}</Badge>
                      ))}
                    </div>
                    <h3 className="text-xl font-bold">{strand.title}</h3>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Assessments</p>
                    <p className="text-2xl font-bold">{strand.assessment_count}</p>
                    <p className="text-sm text-muted-foreground mt-1">{strand.total_marks} marks</p>
                  </div>
                </div>

                {strand.units && strand.units.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <BookOpen className="h-4 w-4 text-blue-600" /> Units Covered
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {strand.units.map((unit, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          Unit {unit}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

// Dynamic Blueprints Tab Component
export const DynamicBlueprintsTab = ({ curriculumData }) => {
  if (!curriculumData || !curriculumData.terms) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <p className="text-muted-foreground">No assessment data available.</p>
        </CardContent>
      </Card>
    );
  }

  // Collect all assessments across all terms
  const allAssessments = curriculumData.terms.flatMap(term => 
    term.assessments.map(a => ({ ...a, term: term.term }))
  );

  // Group by strand
  const assessmentsByStrand = {};
  allAssessments.forEach(assessment => {
    // Extract strand from title (e.g., "Year 12 CS - Strand 1, Unit 1.1.1")
    const match = assessment.title.match(/Strand (\d+)/);
    const strandNum = match ? match[1] : 'Other';
    
    if (!assessmentsByStrand[strandNum]) {
      assessmentsByStrand[strandNum] = [];
    }
    assessmentsByStrand[strandNum].push(assessment);
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Assessment Blueprints: {curriculumData.class.name}</CardTitle>
          <CardDescription>
            Detailed breakdown of all assessments by strand
          </CardDescription>
        </CardHeader>
      </Card>

      {Object.keys(assessmentsByStrand).sort().map(strandNum => (
        <Card key={strandNum}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Strand {strandNum} Assessments
                </CardTitle>
                <CardDescription>
                  {assessmentsByStrand[strandNum].length} assessments
                </CardDescription>
              </div>
              <Badge className="bg-primary">
                {assessmentsByStrand[strandNum].reduce((sum, a) => sum + (a.marks || 0), 0)} marks
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {assessmentsByStrand[strandNum].map((assessment, idx) => (
                <div key={idx} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="font-semibold">{assessment.title}</h4>
                      {assessment.description && (
                        <p className="text-sm text-muted-foreground mt-1">{assessment.description}</p>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <Badge variant="outline">{assessment.term}</Badge>
                      <p className="text-lg font-bold mt-1">{assessment.marks} marks</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="secondary" className="text-xs capitalize">{assessment.type}</Badge>
                    <Badge variant="outline" className="text-xs capitalize">{assessment.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
